import React, { Fragment, useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import LoginInputField from "../../components/forms/login-input-field";
import NexTechLogo from "../../assets/icons/nextech-logo";
import erpIllustration from "../../assets/images/login-erp-illustration.png";
import LoadingButtonSpinner from "../../components/helper-components/loading-button-spinner";
import {
  ArrowRight,
  Truck,
  BarChart3,
  Boxes,
  IndianRupee,
  ReceiptText,
  ScanBarcode,
  Tags,
  Warehouse,
  Calculator,
  Clock,
  FileText,
  Lock,
  Mail,
  Package,
  PieChart,
  Settings,
  ShieldCheck,
  ShoppingCart,
  TrendingUp,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { storeLoginAction } from "../../redux/actions/auth-action";
import errorToast from "../../components/helper-components/error-toast";
import successToast from "../../components/helper-components/success-toast";
import { adminRolesModAction } from "../../redux/actions/general-action";

export default function UserLogin({
  isContainer = true,
  containerClass = "relative min-h-screen lg:h-screen lg:overflow-hidden w-full flex bg-[var(--slide-panel-special-bg)]",
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const identifyInputType = (input) => {
    if (/^\d{10}$/.test(input)) return "phone"; // Matches a 10-digit phone number
    if (/^[a-zA-Z0-9_]{3,}$/.test(input)) return "username"; // Matches username (alphanumeric, at least 3 chars)
    if (/^\S+@\S+\.\S+$/.test(input)) return "email"; // Matches email format
    return "invalid";
  };
  const initialValues = {
    user_email: "",
    password: "",
    user_check: false,
  };
  const validationSchema = {
    user_input: Yup.string()
      .required("Email, phone number, or username is required")
      .test(
        "is-valid-input",
        "Must be a valid email, phone number, or username",
        (value) => identifyInputType(value) !== "invalid"
      ),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Must be atleast 8 characters long"),
    user_check: Yup.boolean(),
  };
  const Container = isContainer ? "div" : Fragment;

  const onSubmit = (values, { resetForm }) => {
    setLoading(true);
    dispatch(
      storeLoginAction(
        {
          user_email: values?.user_input,
          password: values?.password,
        },
        onSuccess,
        onError
      )
    );
  };
  const onSuccess = (data) => {
    // navigate("/bills");
    successToast(data?.message);
    dispatch(adminRolesModAction(onAdminRolesModSuccess, onAdminRolesModError));
    // window.location.reload(); // Corrected line
  };
  const onError = (data) => {
    setLoading(false);
    errorToast(data?.data?.message);
  };

  const onAdminRolesModSuccess = (data) => {
    setLoading(false);
    const ModPermission = data?.data || [];
    console.log("&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&");
    console.log(ModPermission);
    console.log("&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&");
    localStorage.setItem("ModPermission", JSON.stringify(ModPermission));
        navigate("/bills");
  };
  const onAdminRolesModError = (data) => {};

  return (
    <Container
      {...(isContainer && containerClass ? { className: containerClass } : {})}
    >
      <ErpBackdrop />

      {/* Brand side (large screens) */}
      {/* Fits the viewport on large screens, so the page never scrolls */}
      <aside className="relative z-[1] hidden lg:flex flex-1 flex-col h-full px-12 xl:px-16 py-8 [@media(max-height:820px)]:py-5 min-w-0">
        <BrandHeader />

        <h1 className="mt-8 [@media(max-height:820px)]:mt-5 [@media(max-height:820px)]:text-[38px] text-[44px] xl:text-[52px] leading-[1.08] font-extrabold tracking-tight text-[var(--secondary)]">
          One Platform.
          <br />
          Every Operation<span style={{ color: ACCENT }}>.</span>
        </h1>
        <p className="mt-5 [@media(max-height:820px)]:mt-3 max-w-[440px] text-[16px] font-medium leading-relaxed text-[var(--nav-heading-color)]">
          NexTech ERP connects all your business processes and empowers you to
          grow.
        </p>

        <div className="mt-8 [@media(max-height:820px)]:mt-5 grid grid-cols-4 gap-4 [@media(max-height:820px)]:gap-3 max-w-[580px]">
          {MODULES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="h-[104px] [@media(max-height:820px)]:h-[80px] rounded-2xl bg-white border border-[var(--light-border-color)] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.12)] flex flex-col items-center justify-center gap-3"
            >
              <Icon size={22} strokeWidth={1.8} color={ACCENT} />
              <span className="text-[14px] font-semibold text-[var(--secondary)]">
                {label}
              </span>
            </div>
          ))}
        </div>

        <div className="flex-1 min-h-0 mt-6 [@media(max-height:820px)]:mt-3 flex items-start">
          <img
            src={erpIllustration}
            alt="NexTech connects sales, stores, inventory and reports"
            draggable={false}
            className="max-h-full w-full max-w-[760px] object-contain object-left-top select-none mix-blend-multiply [mask-image:radial-gradient(ellipse_72%_68%_at_50%_50%,#000_62%,transparent_100%)] [-webkit-mask-image:radial-gradient(ellipse_72%_68%_at_50%_50%,#000_62%,transparent_100%)]"
          />
        </div>
      </aside>

      {/* Sign-in side */}
      <section className="relative z-[1] w-full lg:w-[46%] xl:w-[42%] lg:h-full lg:overflow-y-auto flex flex-col px-6 py-8 [@media(max-height:820px)]:py-4">
        {/* my-auto centres without clipping the top when the panel scrolls */}
        <div className="my-auto w-full flex flex-col items-center">
        <div className="lg:hidden mb-8">
          <BrandHeader />
        </div>

        <div className="w-full max-w-[560px] bg-white rounded-[24px] border border-[var(--light-border-color)] shadow-[0_2px_4px_rgba(16,24,40,0.04),0_24px_48px_-16px_rgba(16,24,40,0.14)] overflow-hidden">
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={Yup.object(validationSchema)}
            onSubmit={onSubmit}
          >
            {({ values }) => {
              return (
                <Form className="px-8 sm:px-12 pt-10 pb-8 [@media(max-height:820px)]:pt-6 [@media(max-height:820px)]:pb-5">
                  <div className="flex justify-center">
                    <NexTechLogo size={56} className="[@media(max-height:820px)]:w-[44px] [@media(max-height:820px)]:h-[44px]" />
                  </div>
                  <h2 className="mt-6 [@media(max-height:820px)]:mt-3 text-center text-[28px] [@media(max-height:820px)]:text-[24px] font-bold text-[var(--secondary)]">
                    Welcome back
                  </h2>
                  <p className="mt-2 text-center text-[15px] font-medium text-[var(--nav-heading-color)]">
                    Sign in to your NexTech account
                  </p>

                  <div className="mt-8 [@media(max-height:820px)]:mt-5">
                    <IconField icon={Mail}>
                      <LoginInputField
                        type="text"
                        name="user_input"
                        placeholder="Email, phone number, or username"
                        label="Email, Phone, or Username"
                        labelClass={LABEL_CLASS}
                        defaultInputClass={INPUT_CLASS}
                        containerClass="mb-1"
                        isRequired={true}
                      />
                    </IconField>
                    <IconField icon={Lock}>
                      <LoginInputField
                        type="password"
                        name="password"
                        placeholder="Enter your password"
                        label="Password"
                        labelClass={LABEL_CLASS}
                        defaultInputClass={INPUT_CLASS}
                        containerClass="mb-2"
                        isRequired={true}
                        isPasswordEye={true}
                      />
                    </IconField>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-[14px]">
                    <label
                      htmlFor="remember_checkbox"
                      className="flex items-center gap-2 cursor-pointer font-medium text-[var(--label-color)] select-none"
                    >
                      <Field
                        type="checkbox"
                        id="remember_checkbox"
                        name="user_check"
                        className="w-4 h-4 cursor-pointer accent-[var(--primary)]"
                      />
                      Remember for 30 days
                    </label>
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="font-bold text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-7 [@media(max-height:820px)]:mt-5 w-full h-[52px] [@media(max-height:820px)]:h-[46px] rounded-xl bg-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-white text-[16px] font-bold flex items-center justify-center gap-2 shadow-[0_8px_20px_-8px_rgba(39,125,254,0.55)] transition-colors disabled:opacity-80 disabled:cursor-wait"
                  >
                    {loading ? (
                      <LoadingButtonSpinner className="h-5 w-5 border-t-2 border-white" />
                    ) : (
                      <>
                        Sign In <ArrowRight size={18} strokeWidth={2.2} />
                      </>
                    )}
                  </button>

                  <div className="mt-7 [@media(max-height:820px)]:mt-4 flex items-center gap-3 text-[13px] font-medium text-[var(--nav-heading-color)]">
                    <span className="flex-1 border-t border-[var(--light-border-color)]" />
                    <span className="flex items-center gap-1.5">
                      <ShieldCheck size={14} /> Secure access to your business
                    </span>
                    <span className="flex-1 border-t border-[var(--light-border-color)]" />
                  </div>
                </Form>
              );
            }}
          </Formik>

          <div className="grid grid-cols-3 gap-3 px-6 sm:px-8 py-5 [@media(max-height:820px)]:py-3 bg-[var(--slide-panel-special-bg)] border-t border-[var(--light-border-color)]">
            {HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-2.5 min-w-0">
                <Icon size={16} strokeWidth={2} color={ACCENT} className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[14px] font-bold text-[var(--secondary)]">
                    {title}
                  </div>
                  <div className="text-[12px] leading-snug font-medium text-[var(--nav-heading-color)]">
                    {desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 [@media(max-height:820px)]:mt-3 text-[13px] font-medium text-[var(--nav-heading-color)]">
          © {new Date().getFullYear()}{" "}
          <span className="font-bold text-[var(--secondary)]">NexTech</span>. All
          rights reserved.
        </div>
        </div>
      </section>
    </Container>
  );
}

// App theme tokens (index.css)
const ACCENT = "var(--primary)";
const NAVY = "var(--create-table-header-bg)";

const LABEL_CLASS = "whitespace-nowrap flex text-[14px] font-bold text-[var(--secondary)]";

// Room on the left for the field icon, brand-blue focus ring
const INPUT_CLASS =
  "inputField text-value-color focus_outline__none !pl-11 !rounded-xl focus:!border-[var(--primary)] focus:!shadow-[0_0_0_3px_rgba(39,125,254,0.15)] transition-shadow";

const MODULES = [
  { icon: TrendingUp, label: "Sales" },
  { icon: ShoppingCart, label: "Purchase" },
  { icon: Package, label: "Inventory" },
  { icon: FileText, label: "Billing" },
  { icon: Users, label: "Parties" },
  { icon: Calculator, label: "Accounting" },
  { icon: PieChart, label: "Reports" },
  { icon: Settings, label: "Settings" },
];

const HIGHLIGHTS = [
  { icon: ShieldCheck, title: "Secure", desc: "Protected sign-in" },
  { icon: Clock, title: "Reliable", desc: "Built for daily operations" },
  { icon: TrendingUp, title: "Scalable", desc: "Grows with your stores" },
];

const BrandHeader = () => (
  <div className="flex items-center gap-3">
    <NexTechLogo size={52} />
    <div className="leading-none">
      {/* Wordmark in the logo's colours: name = upper stroke, tagline =
          lower orange stroke. */}
      <div className="text-[36px] font-extrabold tracking-[0.02em] text-[var(--brand-logo-dark)]">
        NEXTECH
      </div>
      <div className="mt-1.5 text-[11px] font-semibold tracking-[0.32em] text-[var(--brand-logo-accent)]">
        RETAIL MANAGEMENT SUITE
      </div>
    </div>
  </div>
);

// Puts an icon inside the input. Offset = label height (16px line + 8px gap) + half the 2.9rem input.
const IconField = ({ icon: Icon, children }) => (
  <div className="relative">
    <Icon
      size={17}
      strokeWidth={1.8}
      className="absolute left-4 top-[39px] text-[var(--placeholder-color)] pointer-events-none z-[1]"
    />
    {children}
  </div>
);

// ---------- ERP backdrop: blueprint grid, soft glows and a living data flow ----------

// Edge icons that gently float
const BACKDROP_ICONS = [
  { icon: Warehouse, left: "36%", top: "3%", size: 42, rotate: 6, dur: 7 },
  { icon: IndianRupee, left: "71%", top: "4%", size: 36, rotate: -14, dur: 6 },
  { icon: Calculator, left: "93%", top: "6%", size: 40, rotate: 14, dur: 8 },
  { icon: ShoppingCart, left: "98%", top: "28%", size: 44, rotate: -10, dur: 6.5 },
  { icon: Tags, left: "96%", top: "52%", size: 38, rotate: -12, dur: 7.5 },
  { icon: Package, left: "97%", top: "82%", size: 40, rotate: 8, dur: 6 },
  { icon: ScanBarcode, left: "66%", top: "95%", size: 36, rotate: -8, dur: 8.5 },
];

// Flow graph in a 1920x940 design space (scaled with "slice", so it stays round)
const FLOW_HUBS = [
  { id: "scan", icon: ScanBarcode, x: 790, y: 120 },
  { id: "bill", icon: ReceiptText, x: 1110, y: 230 },
  { id: "stock", icon: Boxes, x: 930, y: 400 },
  { id: "report", icon: BarChart3, x: 1120, y: 560 },
  { id: "ship", icon: Truck, x: 930, y: 720 },
  { id: "pay", icon: IndianRupee, x: 1130, y: 850 },
];

const FLOW_PATHS = [
  { d: "M655 440 C 740 450, 850 420, 930 400", dur: 6, packets: 1 }, // tiles -> inventory
  { d: "M790 120 C 760 220, 900 300, 930 400", dur: 5.5, packets: 2 },
  { d: "M1110 230 C 1060 300, 980 320, 930 400", dur: 5, packets: 1 },
  { d: "M930 400 C 980 480, 1080 480, 1120 560", dur: 5, packets: 2 },
  { d: "M930 400 C 860 520, 880 640, 930 720", dur: 6.5, packets: 1 },
  { d: "M930 720 C 990 800, 1060 830, 1130 850", dur: 5.5, packets: 1 },
  { d: "M1110 230 C 1160 260, 1190 300, 1250 320", dur: 4, packets: 1 }, // into the card
  { d: "M1120 560 C 1160 540, 1190 520, 1250 500", dur: 4, packets: 1 }, // into the card
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const DataFlow = () => {
  const animate = !prefersReducedMotion();
  return (
    <svg
      viewBox="0 0 1920 940"
      preserveAspectRatio="xMidYMid slice"
      className="hidden min-[1536px]:block absolute inset-0 w-full h-full"
    >
      <defs>
        <radialGradient id="flowPacket">
          <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.9" />
          <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* dashed links, slowly drifting like a live feed */}
      {FLOW_PATHS.map(({ d }, i) => (
        <path
          key={`line-${i}`}
          id={`flow-path-${i}`}
          d={d}
          fill="none"
          stroke="var(--primary)"
          strokeOpacity="0.2"
          strokeWidth="1.5"
          strokeDasharray="4 7"
          strokeLinecap="round"
        >
          {animate && (
            <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.8s" repeatCount="indefinite" />
          )}
        </path>
      ))}

      {/* data packets travelling along the links */}
      {animate &&
        FLOW_PATHS.flatMap(({ dur, packets }, i) =>
          Array.from({ length: packets }, (_, p) => {
            const begin = `${(i * 0.7 + (p * dur) / packets).toFixed(2)}s`;
            return (
              <g key={`packet-${i}-${p}`} opacity="0">
                <circle r="10" fill="url(#flowPacket)" />
                <circle r="3.5" fill="var(--primary)" />
                <animateMotion dur={`${dur}s`} begin={begin} repeatCount="indefinite">
                  <mpath href={`#flow-path-${i}`} />
                </animateMotion>
                <animate
                  attributeName="opacity"
                  values="0;0.85;0.85;0"
                  keyTimes="0;0.12;0.85;1"
                  dur={`${dur}s`}
                  begin={begin}
                  repeatCount="indefinite"
                />
              </g>
            );
          })
        )}

      {/* module hubs with a soft pulse */}
      {FLOW_HUBS.map(({ id, icon: Icon, x, y }, i) => (
        <g key={id} transform={`translate(${x} ${y})`}>
          {animate && (
            <circle r="26" fill="none" stroke="var(--primary)" strokeWidth="1.5">
              <animate attributeName="r" values="26;46" dur="3.2s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" values="0.35;0" dur="3.2s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
            </circle>
          )}
          <circle r="26" fill="#ffffff" fillOpacity="0.85" stroke="var(--primary)" strokeOpacity="0.22" strokeWidth="1.5" />
          <Icon x={-11} y={-11} size={22} strokeWidth={1.6} color={NAVY} opacity={0.55} />
        </g>
      ))}
    </svg>
  );
};

const ErpBackdrop = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden select-none">
    <style>{`
      @keyframes erpFloat { 0%, 100% { translate: 0 0; } 50% { translate: 0 -12px; } }
      .erp-float { animation: erpFloat var(--float-dur, 7s) ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) { .erp-float { animation: none; } }
    `}</style>
    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(39,125,254,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(39,125,254,0.07)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:radial-gradient(ellipse_80%_80%_at_55%_45%,#000_30%,transparent_90%)] [-webkit-mask-image:radial-gradient(ellipse_80%_80%_at_55%_45%,#000_30%,transparent_90%)]" />
    <div className="absolute -top-40 left-[38%] w-[520px] h-[520px] rounded-full bg-[var(--primary)] opacity-[0.07] blur-3xl" />
    <div className="absolute -bottom-48 right-[-120px] w-[560px] h-[560px] rounded-full bg-[var(--create-table-header-bg)] opacity-[0.06] blur-3xl" />

    <DataFlow />

    {BACKDROP_ICONS.map(({ icon: Icon, left, top, size, rotate, dur }, i) => (
      <Icon
        key={i}
        size={size}
        strokeWidth={1.3}
        color={NAVY}
        className="erp-float absolute opacity-[0.08] -translate-x-1/2 -translate-y-1/2"
        style={{ left, top, rotate: `${rotate}deg`, "--float-dur": `${dur}s`, animationDelay: `${-i * 1.3}s` }}
      />
    ))}
  </div>
);
